/**
 * CheckboxCheckedIcon / CheckboxIndeterminateIcon
 *
 * Replaces MUI's default `check_box` and `indeterminate_check_box` icons to
 * make the inner mark readable on brand-colored checkboxes.
 *
 * WHY THIS EXISTS
 * ---------------
 * MUI's built-in checked/indeterminate icons are **single SVG paths** that
 * draw both the filled box and the inner mark as one shape.  The mark is a
 * "cutout" — a transparent hole — so it shows whatever is underneath it at
 * render time.  On a white page that means a white mark, which is fine for
 * dark primary colors (e.g. Perkins blue #01529C).
 *
 * For the Caterpillar theme the primary color is `#FFCC11` (bright yellow).
 * A white cutout on yellow is nearly invisible; changing the primary color is
 * not an option (brand requirement).
 *
 * SOLUTION
 * --------
 * Render the same geometry as **two separate `<path>` elements**:
 *   1.  Box fill  – `fill: currentColor`  → inherits the primary brand color
 *   2.  Mark      – `fill: contrastText`  → explicitly uses the palette's
 *       contrast color so it is always legible against the box fill.
 *
 * The `contrastText` value comes from `palette.primary.contrastText`, which
 * MUI derives automatically from the primary hue to satisfy WCAG AA contrast.
 *
 * USAGE
 * -----
 * Pass `contrastText` from `palette.primary.contrastText` at theme-build time:
 *
 * ```ts
 * import { createCheckedIcon, createIndeterminateIcon } from "./overrides/CheckboxCheckedIcon";
 *
 * MuiCheckbox: {
 *   defaultProps: {
 *     checkedIcon: createCheckedIcon(primary.contrastText),
 *     indeterminateIcon: createIndeterminateIcon(primary.contrastText),
 *   },
 * }
 * ```
 */

import { SvgIcon } from '@mui/material'
import React from 'react'

export function createCheckedIcon(contrastText: string) {
  return React.createElement(
    SvgIcon,
    null,
    // Filled box — inherits current color (primary.main)
    React.createElement('path', {
      d: 'M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z',
      fill: 'currentColor',
    }),
    // Checkmark — explicitly colored for contrast
    React.createElement('path', {
      d: 'M10 17l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
      fill: contrastText,
    }),
  )
}

export function createIndeterminateIcon(contrastText: string) {
  return React.createElement(
    SvgIcon,
    null,
    // Filled box — inherits current color (primary.main)
    React.createElement('path', {
      d: 'M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z',
      fill: 'currentColor',
    }),
    // Dash — explicitly colored for contrast
    React.createElement('path', {
      d: 'M17 13H7v-2h10v2z',
      fill: contrastText,
    }),
  )
}
